import React, { createContext, useContext, useEffect, useState } from 'react';
import { authApi } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('fosen_user') || 'null');
    } catch (error) {
      return null;
    }
  });
  const [loading, setLoading] = useState(() => Boolean(localStorage.getItem('fosen_access_token')));

  useEffect(() => {
    const token = localStorage.getItem('fosen_access_token');
    const refreshToken = localStorage.getItem('fosen_refresh_token');
    if (!token && !refreshToken) return;

    const restoreSession = async () => {
      try {
        let response;
        try {
          response = await authApi.me();
        } catch (error) {
          if (!refreshToken) throw error;
          const refreshResponse = await authApi.refresh(refreshToken);
          const nextAccessToken = refreshResponse.data?.data?.accessToken;
          if (!nextAccessToken) throw error;
          localStorage.setItem('fosen_access_token', nextAccessToken);
          if (localStorage.getItem('adminUser') || localStorage.getItem('adminToken')) localStorage.setItem('adminToken', nextAccessToken);
          response = await authApi.me();
        }
        const nextUser = response.data?.data;
        setUser(nextUser);
        localStorage.setItem('fosen_user', JSON.stringify(nextUser));
        if (nextUser?.role && nextUser.role !== 'customer') localStorage.setItem('adminUser', JSON.stringify(nextUser));
      } catch (error) {
        // Keep the stored user visible unless refresh is definitely invalid; manual logout still clears everything.
        if (!refreshToken) setUser(null);
      } finally {
        setLoading(false);
      }
    };

    restoreSession();
  }, []);

  const saveSession = (payload) => {
    localStorage.setItem('fosen_access_token', payload.accessToken);
    localStorage.setItem('fosen_refresh_token', payload.refreshToken);
    localStorage.setItem('fosen_user', JSON.stringify(payload.user));
    setUser(payload.user);
    // if admin user, also mirror session into admin-specific storage keys
    if (payload.user && payload.user.role && payload.user.role !== 'customer') {
      localStorage.setItem('adminToken', payload.accessToken);
      localStorage.setItem('adminRefreshToken', payload.refreshToken);
      localStorage.setItem('adminUser', JSON.stringify(payload.user));
    }
  };

  const login = async (payload) => {
    setLoading(true);
    try {
      const response = await authApi.login(payload);
      if (response.data?.requiresOtp) return response.data;
      saveSession(response.data.data);
      return response.data.data.user;
    } finally {
      setLoading(false);
    }
  };

  const adminLogin = async (payload) => {
    setLoading(true);
    try {
      const response = await authApi.adminLogin(payload);
      if (response.data?.requiresOtp) return response.data;
      saveSession(response.data.data);
      return response.data.data.user;
    } finally {
      setLoading(false);
    }
  };

  const signup = async (payload, isAdmin = false) => {
    setLoading(true);
    try {
      const response = isAdmin ? await authApi.registerAdmin(payload) : await authApi.register(payload);
      if (response.data?.requiresOtp) return response.data;
      saveSession(response.data.data);
      return response.data.data.user;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    const refreshToken = localStorage.getItem('fosen_refresh_token');
    if (refreshToken) authApi.logout({ refreshToken }).catch(() => {});
    localStorage.removeItem('fosen_access_token');
    localStorage.removeItem('fosen_refresh_token');
    localStorage.removeItem('fosen_user');
    setUser(null);
  };

  const updateLocalUser = (nextUser) => {
    localStorage.setItem('fosen_user', JSON.stringify(nextUser));
    setUser(nextUser);
    if (nextUser?.role && nextUser.role !== 'customer') {
      localStorage.setItem('adminUser', JSON.stringify(nextUser));
    }
  };

  // Also clear admin-specific keys when logging out
  const logoutAll = () => {
    logout();
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminUser');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, adminLogin, signup, completeSession: saveSession, updateLocalUser, logout: logoutAll, isAdmin: Boolean(user?.role && user.role !== 'customer') }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
